<template>
  <div>
    <!-- Exibe o LoadingSpinner enquanto isLoading for true -->
    <div v-if="isLoading">
      <LoadingSpinner />
    </div>
    <!-- Quando não estiver carregando, exibe o conteúdo -->
    <div v-else>
      <!-- Verifica se há matrícula para exibir os dados -->
      <div v-if="matricula" class="card card-body shadow-sm mt-0">
        <div class="header d-flex justify-content-between align-items-center">
          <h4 class="form-title">
            Detalhes de Pós-venda - Matrícula:
            <span v-if="matricula.id"> {{ matricula.id }}</span>
          </h4>
          <button class="btn btn-secondary" @click="cancelar">Voltar</button>
        </div>

        <!-- Bloco de Dados do Contratante (Responsável) -->
        <div class="form-content mt-3">
          <FormSection title="Dados do Contratante">
            <p><strong>Nome:</strong> {{ responsavel.nome }}</p>
            <p><strong>CPF:</strong> {{ responsavelCPFFormatado }}</p>
            <p><strong>RG:</strong> {{ responsavel.rg }}</p>
            <p><strong>Data de Nascimento:</strong> {{ responsavel.data_nascimento }}</p>
            <p><strong>Sexo:</strong> {{ responsavelSexoFormatado }}</p>
            <p><strong>Celular:</strong> {{ responsavel.celular }}</p>
            <p><strong>E-mail:</strong> {{ responsavel.email }}</p>
            <p><strong>Endereço:</strong> {{ responsavel.endereco }}</p>
            <p><strong>Número:</strong> {{ responsavel.numero }}</p>
            <p><strong>Complemento:</strong> {{ responsavel.complemento }}</p>
            <p><strong>Bairro:</strong> {{ responsavel.bairro }}</p>
            <p><strong>Estado:</strong> {{ responsavel.estado_nome || responsavel.estado_cod }}</p>
            <p><strong>Município:</strong> {{ responsavel.municipio_nome || responsavel.municipio_cod }}</p>
            <p><strong>CEP:</strong> {{ responsavel.cep }}</p>
            <p><strong>Nacionalidade:</strong> {{ responsavel.nacionalidade || 'Não informado' }}</p>
            <p><strong>Naturalidade:</strong> {{ responsavel.naturalidade || 'Não informado' }}</p>
            <p><strong>Observações:</strong> {{ responsavel.observacoes }}</p>
          </FormSection>
        </div>

        <!-- Bloco de Dados do Aluno -->
        <div class="form-content mt-3">
          <FormSection title="Dados do Aluno">
            <p><strong>Nome:</strong> {{ aluno.nome }}</p>
            <p><strong>CPF:</strong> {{ alunoCPFFormatado }}</p>
            <p><strong>RG:</strong> {{ aluno.rg }}</p>
            <p><strong>Data de Nascimento:</strong> {{ aluno.data_nascimento }}</p>
            <p><strong>Sexo:</strong> {{ alunoSexoFormatado }}</p>
            <p><strong>Celular:</strong> {{ aluno.celular }}</p>
            <p><strong>E-mail:</strong> {{ aluno.email }}</p>
            <p><strong>Endereço:</strong> {{ aluno.endereco }}</p>
            <p><strong>Número:</strong> {{ aluno.numero }}</p>
            <p><strong>Complemento:</strong> {{ aluno.complemento }}</p>
            <p><strong>Bairro:</strong> {{ aluno.bairro }}</p>
            <p><strong>Estado:</strong> {{ aluno.estado_nome || aluno.estado_cod }}</p>
            <p><strong>Município:</strong> {{ aluno.municipio_nome || aluno.municipio_cod }}</p>
            <p><strong>CEP:</strong> {{ aluno.cep }}</p>
            <p><strong>Nacionalidade:</strong> {{ aluno.nacionalidade || 'Não informado' }}</p>
            <p><strong>Naturalidade:</strong> {{ aluno.naturalidade || 'Não informado' }}</p>
            <p><strong>Observações:</strong> {{ aluno.observacoes }}</p>
          </FormSection>
        </div>

        <!-- Dados do Plano de Matrícula -->
        <div class="form-content mt-3">
          <FormSection title="Dados do Plano de Matrícula">
            <p><strong>Início da Vigência:</strong> {{ utils.formatarData(matricula.inicio_vigencia) }}</p>
            <p><strong>Término da Vigência:</strong> {{ utils.formatarData(matricula.termino_vigencia) }}</p>
            <p>
              <strong>Status: </strong>
              <span :style="{ color: statusColor }">
                {{ statusExibido }}
              </span>
            </p>
            <p><strong>Categoria:</strong> {{ getCategoriaNome(matricula.categoria_id) }}</p>
            <p><strong>Tipo de Plano:</strong> {{ getTipoPlanoDescricao(matricula.tipo_plano_id) }}</p>
            <p><strong>Aulas por Semana:</strong> {{ plano.aulas_semana }}</p>
            <p><strong>Nível:</strong> {{ getNivelDescricao(matricula.nivel_id) }}</p>
            <p><strong>Qtd. de Parcelas:</strong> {{ plano.quantidade_parcelas }}</p>
            <p><strong>Turmas:</strong></p>
            <ul>
              <li v-for="(turma, index) in turmasDisponiveis" :key="index">
                {{ turma.turma_completa }}
              </li>
            </ul>
          </FormSection>
        </div>

        <!-- Dados Financeiros -->
        <div class="form-content mt-3">
          <FormSection title="Dados Financeiros">
            <p>
              <strong>Data da Matrícula:</strong>
              {{ utils.formatarData(matricula.data_matricula) }}
            </p>
            <p>
              <strong>Valor:</strong>
              {{ globalUtils.formatarValor(matricula.valor) }}
            </p>
            <p>
              <strong>Desconto (%):</strong>
              {{ matricula.desconto }}
            </p>
            <p>
              <strong>Valor com Desconto:</strong>
              {{ globalUtils.formatarValor(matricula.valor_com_desconto) }}
            </p>
            <p>
              <strong>Forma de Pagamento:</strong>
              {{ formasPagamento.find(fp => fp.id === matricula.pagamento_matricula.forma_pagamento_id)?.nome || 'Não informado' }}
            </p>
            <template v-if="isCheque(matricula.forma_pagamento_id) && pagamentoMatricula">
              <p>
                <strong>Banco nº:</strong> {{ pagamentoMatricula.banco_numero }}
              </p>
              <p>
                <strong>Agência:</strong> {{ pagamentoMatricula.agencia }}
              </p>
              <p>
                <strong>Cheque nº:</strong> {{ pagamentoMatricula.cheque_numero }}
              </p>
            </template>
            <p>
              <strong>Conta Financeira:</strong> {{ contaFinanceiraFormatada }}
            </p>
            <p>
              <strong>Data de Vencimento:</strong>
              {{ utils.formatarData(matricula.pagamento_matricula.data_vencimento) }}
            </p>
            <p>
              <strong>Data de Pagamento: </strong>
              <span v-if="matricula.pagamento_matricula.data_pagamento">
                {{ utils.formatarData(matricula.pagamento_matricula.data_pagamento) }}
              </span>
              <span v-else style="color: red;"> Pendente</span>
            </p>
            <p>
              <strong>Convênio:</strong>
              {{ convenios.find(c => c.id === matricula.convenio_id)?.nome || 'Não informado' }}
            </p>
          </FormSection>
        </div>

        <!-- Seção Mensalidade -->
        <div class="form-content mt-3">
          <FormSection title="Mensalidade">
            <p>
              <strong>Valor:</strong>
              {{ globalUtils.formatarValor(matricula.valor_mensalidade || 0) }}
            </p>
            <p>
              <strong>Desconto (%):</strong>
              {{ matricula.desconto_mensalidade }}
            </p>
            <p>
              <strong>Valor com Desconto:</strong>
              {{ globalUtils.formatarValor(matricula.valor_com_desconto_mensalidade) }}
            </p>
            <p>
              <strong>1º Vencimento:</strong>
              {{ utils.formatarData(matricula.primeiro_vencimento) }}
            </p>
            <p>
              <strong>Qtd. de Pagamentos:</strong>
              {{ matricula.quantidade_pagamentos }}
            </p>
            <p>
              <strong>Valor Total:</strong>
              {{ globalUtils.formatarValor(matricula.valor_total_mensalidade) }}
            </p>
            <div class="table-responsive mt-1" v-if="matricula.pagamentos && matricula.pagamentos.length > 0">
              <table class="table table-bordered">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Valor</th>
                    <th>Vencimento</th>
                    <th>Pagamento</th>
                    <th>Forma de Pagamento</th>
                    <th>Conta Financeira</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(pagamento, index) in matricula.pagamentos"
                    :key="pagamento?.id"
                  >
                    <td>{{ index + 1 }}</td>
                    <td>{{ pagamento.valor ? globalUtils.formatarValor(pagamento.valor) : '' }}</td>
                    <td>{{ pagamento.data_vencimento ? utils.formatarData(pagamento.data_vencimento) : '' }}</td>
                    <td>
                      <template v-if="pagamento.data_pagamento">
                        {{ utils.formatarData(pagamento.data_pagamento) }}
                      </template>
                      <template v-else>
                        <span style="color: red;">Pendente</span>
                      </template>
                    </td>
                    <td>
                      <template v-if="isCheque(pagamento.forma_pagamento_id)">
                        Cheque – Banco nº: {{ pagamento.banco_numero }} | Agência: {{ pagamento.agencia }} | Cheque nº: {{ pagamento.cheque_numero }}
                      </template>
                      <template v-else>
                        {{ formasPagamento.find(fp => fp.id === pagamento.forma_pagamento_id)?.nome || 'Não informado' }}
                      </template>
                    </td>
                    <td>{{ contaFormatada(pagamento?.conta_financeira_id) }}</td>
                  </tr>
                </tbody>
              </table>
              <p></p>
              <p>
                <strong>Observações:</strong>
                {{ matricula.observacoes }}
              </p>
            </div>
          </FormSection>
        </div>

        <!-- Seção Histórico de Trocas de Plano -->
        <div class="form-content mt-3">
          <FormSection title="Histórico de Trocas de Plano">
            <div class="table-responsive mt-1" v-if="historicoTrocasPlanos && historicoTrocasPlanos.length > 0">
              <table class="table table-bordered">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Contrato</th>
                    <th>Plano</th>
                    <th>Cadastro</th>
                    <th>Vigência</th>
                    <th>Por</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(troca, index) in historicoTrocasPlanos" :key="troca.id">
                    <td>{{ index + 1 }}</td>
                    <td>{{ troca.numero_contrato }}</td>
                    <td>{{ troca.tipo_plano }}</td>
                    <td>{{ troca.cadastro_c04_br || globalUtils.formatarData(troca.created_at) }}</td>
                    <td>
                      {{ globalUtils.formatarData(troca.inicio_vigencia) }} a
                      {{ globalUtils.formatarData(troca.termino_vigencia) }}
                    </td>
                    <td>{{ troca.nome_usuario }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else>
              <p>Nenhum histórico de troca de plano registrado.</p>
            </div>
          </FormSection>
        </div>

 
        <!-- Bloco de Histórico de Pós-venda -->
        <div class="historico-container mt-3" v-if="registroPosvenda">
          <div class="historico-header">
            <h5 class="historico-title">Histórico de Pós-venda</h5>
          </div>
          <p></p>
          <div class="historico-content">
            <p style="display: block; margin-bottom: 0.5rem;">
              Última ação realizada em {{ utils.formatarData(registroPosvenda.data_contato) }} por {{ registroPosvenda.nome_usuario }} e agendada para {{ utils.formatarData(registroPosvenda.proximo_contato) }}
            </p>
            <div class="historico-header">
              <h5 class="historico-title" >{{ registroPosvenda.descricao }}</h5>
            </div>
            <p></p>
            <div class="table-responsive mt-1" v-if="historicoPosvenda && historicoPosvenda.length > 0">
              <table class="table table-bordered">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Contato</th>
                    <th>Descrição</th>
                    <th>Agendamento</th>
                    <th>Por</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(hist, index) in historicoPosvenda" :key="hist.id">
                    <td>{{ index + 1 }}</td>
                    <td>{{ utils.formatarData(hist.data_contato) }}</td>
                    <td>{{ hist.descricao }}</td>
                    <td>{{ utils.formatarData(hist.proximo_contato) }}</td>
                    <td>{{ hist.nome_usuario }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else>
              <!-- <p>Nenhum histórico de pós-venda registrado.</p> -->
            </div>
          </div>
        </div>

      </div>
      <!-- Caso não haja matrícula, você pode exibir uma mensagem ou outro conteúdo -->
      <div v-else>
        <p>Nenhuma matrícula encontrada.</p>
      </div>
    </div>
  </div>
</template>
  
  <script>
  import { reactive, ref, onMounted, watch, computed } from "vue";
  import { useRoute, useRouter } from "vue-router"; // 🔹 Importações conforme original
  import axios from "@/services/axios";
  import globalUtils from "@/utils/globalUtils.js";
  import FormSection from "@/components/Layout/FormSection.vue";
 
  
  export default {
    components: { FormSection },
  
    setup() {
  
      const route = useRoute(); // conforme original
      const router = useRouter(); // conforme original
  
      const responsavel = reactive({});
      const aluno = reactive({});

      const isLoading = ref(true);
      const pagamentoMatricula = ref(null);       // ← declara no setup()
  
      const matricula = reactive({
  
        aluno_nome: '',
        responsavel_nome: '',
  
        categoria_id: '',
        tipo_plano_id: '',
        nivel_id: '',
        inicio_vigencia: '',
        termino_vigencia: '',
        aulas_semana: 0, // Número de aulas por semana
        quantidade_parcelas: 0,
        turmas: [],
  
        data_matricula: new Date().toISOString().split("T")[0],
        valor: "",
        desconto: 0,
        valor_com_desconto: "",
        forma_pagamento_id: "",
        data_vencimento: "",
        convenio_id: "",
        pagamento_pendente: true,
  
        desconto_mensalidade: 0,
        valor_com_desconto_mensalidade: 0,
        primeiro_vencimento: "",
        quantidade_pagamentos: 0,
        valor_total_mensalidade: "",
        pagamentos: [],
        posvendas: [],
  
      });
  
      const historicoPosvenda = ref([]);

      const categorias = ref([]);
      const tiposPlano = ref([]);
      const niveis = ref([]);
      const turmas = ref([]);
      const novaTurma = reactive({ turma_id: '' });
      const aulasSemanaOptions = ref([1, 2, 3, 4, 5, 6]); // Número de aulas disponíveis por semana
      const parcelasOptions = ref(Array.from({ length: 12 }, (_, i) => i + 1)); // De 1 a 12 parcelas
      const turmasDisponiveis = ref([]); // Lista de turmas filtradas
  
      const formasPagamento = ref([]);
      const convenios = ref([]);
  
      const historicoTrocas = ref([]);
      const historicoTrocasPlanos = ref([]);
  
      // Computada para obter o registro atual da pós-venda
      // Precisamos encontrar o registro na tabela posvendas onde matricula_id = matricula.id.
      const registroPosvenda = computed(() => {
        if (matricula.posvendas && matricula.posvendas.length > 0 && matricula.id) {
          return matricula.posvendas.find(p => p.matricula_id === matricula.id) || null;
        }
        return null;
      });
    
      // Função para carregar o histórico de pós-venda (tabela posvendas_historico)
      const carregarHistoricoPosvenda = async () => {
        if (registroPosvenda.value && registroPosvenda.value.id) {
          try {
            const response = await axios.get("/operacional/posvendas/posvendas_historico", {
              params: { posvenda_id: registroPosvenda.value.id }
            });
            historicoPosvenda.value = response.data;
          } catch (error) {
            console.error("Erro ao carregar histórico de pós-venda:", error);
          }
        }
      };

      const contasFinanceiras = ref([]);

      const carregarContasFinanceiras = async () => {
        try {
          const { data } = await axios.get('/financeiro/contas-financeiras');
          contasFinanceiras.value = data.data || data;
        } catch (e) {
          console.error(e);
          contasFinanceiras.value = [];
        }
      };

      const contaFinanceiraFormatada = computed(() => {
        const id = matricula.pagamento_matricula.conta_financeira_id;
        if (!id) return 'Não informado';
        const c = contasFinanceiras.value.find(x => String(x.id) === id);
        return c
          ? `${c.apelido || 'Sem apelido'} - ${c.agencia || 'Sem agência'} - ${c.numero_conta || 'Sem número'}`
          : 'Não informado';
      });

      const contaFormatada = (id) => {
        if (!id) return 'Não informado';
        // pega o array interno
        const list = contasFinanceiras.value;
        const c = list.find(x => String(x.id) === String(id));
        if (!c) return 'Não informado';
        return `${c.apelido || 'Sem apelido'} – ag. ${c.agencia || 'Sem agência'} – cc. ${c.numero_conta || 'Sem número'}`;
      };

 
      const carregarHistoricoTrocasPlanos = async () => {
        try {
          // Altere a URL conforme o endpoint correto do seu backend
          const response = await axios.get(`/operacional/matriculas-trocas-planos`, {
            params: { id_matricula: matricula.id }
          });
          historicoTrocasPlanos.value = response.data || [];
        } catch (error) {
          console.error("Erro ao carregar histórico de trocas de plano:", error);
          historicoTrocasPlanos.value = [];
        }
      };
  
  
      const plano = reactive({
        aulas_semana: null,
        quantidade_parcelas: null,
        valor_mensalidade: 0,
      });
  
      const carregarCategorias = async () => {
        try {
          const response = await axios.get('/cadastros/categorias-de-planos');
          const todasCategorias = Array.isArray(response.data) ? response.data : response.data.data || [];
  
          // 🔹 Filtra apenas categorias ativas
          categorias.value = todasCategorias.filter(categoria => categoria.ativo);
  
        } catch (error) {
          console.error('❌ Erro ao carregar categorias de planos:', error);
        }
      };
  
      const carregarTiposPlano = async () => {
        if (!matricula.categoria_id) return; // só carrega com categoria

        try {
          // 1) Planos da categoria (sem paginação)
          const respPlanos = await axios.get('/cadastros/planos-matriculas', {
            params: {
              categoria_id: matricula.categoria_id,
              limit: 'all',
            }
          });
          const planosArray = respPlanos?.data?.data || respPlanos?.data || [];
          const planosAtivos = Array.isArray(planosArray)
            ? planosArray.filter(plano => ('ativo' in plano) ? (plano.ativo === true || plano.ativo === 1) : true)
            : [];

          // 2) Tipos de planos (sem paginação, ordenado por duração no back)
          const respTipos = await axios.get('/cadastros/tipos-planos', {
            params: {
              limit: 'all',
              sort: 'duracao',
              order: 'asc',
            }
          });
          const tiposPlanosLista = respTipos?.data?.data || respTipos?.data || [];

          // 3) Casa plano ativo com tipo pelo nome e monta a lista
          const lista = planosAtivos.map(plano => {
            const tipoEncontrado = tiposPlanosLista.find(
              tipo => (tipo?.nome || '').trim() === (plano?.tipo || '').trim()
            );
            return {
              id: tipoEncontrado ? tipoEncontrado.id : null,
              nome: plano?.tipo || '',
              duracao: tipoEncontrado?.duracao ?? null,
              aulas_semana: plano?.aulas_semana ?? 0,
              quantidade_parcelas: plano?.quantidade_parcelas ?? 0,
              valor_mensalidade: plano?.valor_parcela ?? 0,
            };
          }).filter(t => t.id !== null);

          // 4) Garante ordenação por duração asc no front (fallbacks seguros)
          lista.sort((a, b) => {
            const da = Number.isFinite(a.duracao) ? a.duracao : Number.MAX_SAFE_INTEGER;
            const db = Number.isFinite(b.duracao) ? b.duracao : Number.MAX_SAFE_INTEGER;
            return da - db;
          });

          tiposPlano.value = lista;

          // 5) Se já existe um tipo selecionado, reaplica preenchimentos na matrícula
          if (matricula.tipo_plano_id) {
            const planoSel = tiposPlano.value.find(p => p.id === matricula.tipo_plano_id);
            if (planoSel) {
              matricula.valor_mensalidade = planoSel.valor_mensalidade;
              matricula.quantidade_pagamentos = planoSel.quantidade_parcelas;
              if (typeof calcularValorComDescontoMensalidade === 'function') {
                calcularValorComDescontoMensalidade();
              }
              if (matricula.inicio_vigencia && typeof calcularTerminoVigencia === 'function') {
                calcularTerminoVigencia();
              }
            }
          }
        } catch (error) {
          console.error("❌ Erro ao carregar tipos de planos:", error?.response?.data || error);
          tiposPlano.value = [];
        }
      };
  
      const carregarNiveis = async () => {
        try {
          const response = await axios.get('/cadastros/niveis-turmas');
          const dados = response.data.data || response.data || [];
          niveis.value = Array.isArray(dados) ? dados.filter(n => n.ativo) : [];
  
        } catch (error) {
          console.error('❌ Erro ao carregar níveis:', error);
        }
      };
  
  
      const carregarTurmas = async () => {
        // Certifique-se de que matricula.id esteja definido (ou use a propriedade que contém o id da matrícula)
        if (!matricula.id) return; 
  
        try {
          // Chama o endpoint que retorna as turmas associadas à matrícula
          const response = await axios.get(`/operacional/matriculas-turmas?matricula_id=${matricula.id}`);
          // Aqui espera-se que a resposta contenha os registros com o campo turma_completa já preenchido
          turmasDisponiveis.value = response.data;
        } catch (error) {
          console.error("Erro ao carregar turmas:", error);
          turmasDisponiveis.value = [];
        }
      };
  
      const carregarDadosPlanoMatricula = async () => {
        if (!matricula.categoria_id || !matricula.tipo_plano_id) return; // Se não houver categoria/tipo, não busca
  
        try {
  
          const response = await axios.get(`/cadastros/planos-matriculas`, {
            params: {
              categoria_id: matricula.categoria_id,
              tipo_plano_id: matricula.tipo_plano_id
            }
          });
  
          if (response.data && response.data.aulas_semana !== undefined) {
            matricula.aulas_semana = response.data.aulas_semana; // Não substitua por 1 se for nulo
            matricula.quantidade_parcelas = response.data.quantidade_parcelas ?? 1;
            matricula.quantidade_pagamentos = response.data.quantidade_parcelas ?? 1;
            
          }
        } catch (error) {
          console.error("❌ Erro ao carregar dados do plano de matrícula:", error);
        }
      };
  
      const calcularTerminoVigencia = () => {
        const tipoPlanoSelecionado = tiposPlano.value.find(p => p.id === matricula.tipo_plano_id);
        if (tipoPlanoSelecionado && matricula.inicio_vigencia) {
          const inicio = new Date(matricula.inicio_vigencia + 'T00:00:00');
          inicio.setDate(inicio.getDate() + tipoPlanoSelecionado.duracao);
          matricula.termino_vigencia = inicio.toISOString().split('T')[0];
        }
      };
  
      const removerTurma = (index) => {
        matricula.turmas.splice(index, 1);
      };
  
      const carregarValorMatricula = async () => {
        try {
          // 1) buscar tipos
          const responseTipos = await axios.get("/financeiro/tipos-valores");
          const tiposValores = responseTipos.data.data || responseTipos.data;
          if (!Array.isArray(tiposValores)) {
            console.error("❌ Estrutura inesperada para tipos_valores:", responseTipos.data);
            return;
          }

          // 2) encontrar o tipo "Matrícula"
          const tipoMatricula = tiposValores.find(t => t.nome === "Matrícula");
          if (!tipoMatricula) {
            console.warn("⚠️ Tipo 'Matrícula' não encontrado.");
            return;
          }

          // 3) buscar valores (colocando um limit grande para trazer tudo)
          const responseValores = await axios.get("/financeiro/valores?limit=1000");
          const valores = responseValores.data.data || responseValores.data;
          if (!Array.isArray(valores)) {
            console.error("❌ Estrutura inesperada para valores:", responseValores.data);
            return;
          }

          // 4) encontrar pelo nome
          const valorMatricula = valores.find(v => v.tipo === tipoMatricula.nome);
          if (!valorMatricula) {
            console.warn("⚠️ Nenhum valor encontrado para o tipo 'Matrícula'.");
            return;
          }

          // 5) atualizar o campo (novo cadastro)
          if (!matricula.id) {
            matricula.valor = parseFloat(valorMatricula.valor).toFixed(2);
          }

        } catch (error) {
          console.error("❌ Erro ao carregar valor da matrícula:", error);
        }
      };
  
      const calcularValorComDesconto = () => {
        const valorBase = parseFloat(matricula.valor) || 0;
        const desconto = parseFloat(matricula.desconto) || 0;
        matricula.valor_com_desconto = (valorBase - (valorBase * desconto / 100)).toFixed(2);
  
      };
  
      const atualizarDescontoConvenio = () => {
        const convenioSelecionado = convenios.value.find(c => c.id === matricula.convenio_id);
        if (convenioSelecionado) {
          matricula.desconto = convenioSelecionado.desconto;
          calcularValorComDesconto();
        }
      };
  
      const carregarFormasPagamento = async () => {
          try {
              const response = await axios.get('/cadastros/formas-pagamento');
  
              formasPagamento.value = response.data.map(fp => ({
                  id: fp.id,
                  nome: fp.nome
              }));
  
          } catch (error) {
              console.error("❌ Erro ao carregar formas de pagamento:", error);
          }
      };
  
      const carregarConvenios = async () => {
        try {
          const response = await axios.get("/cadastros/convenios");
  
          // ✅ Garantir que response.data seja um array ou acesse corretamente os dados
          const conveniosData = response.data.data || response.data; 
  
          if (Array.isArray(conveniosData)) {
            convenios.value = conveniosData.filter(c => c.ativo);
          } else {
            console.error("❌ Estrutura inesperada para convenios:", response.data);
            convenios.value = [];
          }
        } catch (error) {
          console.error("❌ Erro ao carregar convenios:", error);
        }
      };
  
  
      const calcularValorComDescontoMensalidade = () => {
  
        const valorBase = parseFloat(matricula.valor_mensalidade) || 0;
        const desconto = parseFloat(matricula.desconto_mensalidade) || 0;
        matricula.valor_com_desconto_mensalidade = (valorBase - (valorBase * desconto / 100)).toFixed(2);
  
        calcularValorTotalMensalidade(); // Garante que o valor total seja atualizado corretamente
      };
  
  
      const calcularValorTotalMensalidade = () => {
  
        const valorDesconto = parseFloat(matricula.valor_com_desconto_mensalidade) || 0;
        const quantidadePagamentos = parseInt(matricula.quantidade_pagamentos) || 1;
  
        matricula.valor_total_mensalidade = (valorDesconto * quantidadePagamentos).toFixed(2);
  
        if (!matricula.id) {
          distribuirParcelasMensalidade();
        }
      };
  
  
      const distribuirParcelasMensalidade = () => {
          if (!matricula.primeiro_vencimento || matricula.pagamentos.length > 0) return; // 🔹 Evita sobrescrever em edição
  
          let dataVencimento = new Date(matricula.primeiro_vencimento);
  
          for (let i = 0; i < plano.quantidade_parcelas; i++) {
              matricula.pagamentos.push({
                  forma_pagamento_id: null,
                  vencimento_pagamento: dataVencimento.toISOString().split("T")[0],
                  valor: matricula.valor_com_desconto_mensalidade,
                  data_pagamento: "",
                  banco_numero: "",
                  agencia: "",
                  cheque_numero: ""
              });
  
              dataVencimento.setMonth(dataVencimento.getMonth() + 1);
          }
      };
  
  
      const podeAdicionarPagamento = computed(() => {
        return matricula.pagamentos.length < matricula.quantidade_pagamentos;
      });
     
      const adicionarPagamento = () => {
        if (matricula.pagamentos.length >= matricula.quantidade_pagamentos) {
          alert("Você já atingiu o limite de parcelas permitido.");
          return;
        }
  
        // Criando um novo pagamento
        matricula.pagamentos.push({
          forma_pagamento_id: null,
          vencimento_pagamento: "",
          valor: 0,
          data_pagamento: "",
          banco_numero: "",
          agencia: "",
          cheque_numero: ""
        });
  
        // 🔄 Recalcula as parcelas incluindo o novo pagamento
        recalcularParcelas();
      };
  
      const recalcularParcelas = () => {
  
        const parcelasPagas = matricula.pagamentos.filter(p => p.data_pagamento).length;
        const parcelasRestantes = matricula.pagamentos.length - parcelasPagas;
  
        if (parcelasRestantes <= 0) return;
  
        let valorTotalRestante = parseFloat(matricula.valor_total_mensalidade) || 0;
  
        // Certificar que os valores de pagamento estão sendo convertidos corretamente
        matricula.pagamentos.forEach(pagamento => {
          if (pagamento.data_pagamento) {
            let valorNumerico = parseFloat(pagamento.valor.toString().replace(/[^\d.,-]/g, "").replace(",", "."));
            if (!isNaN(valorNumerico)) {
              valorTotalRestante -= valorNumerico;
            }
          }
        });
  
        const novoValorParcela = (valorTotalRestante / parcelasRestantes).toFixed(2);
  
        matricula.pagamentos.forEach(pagamento => {
          if (!pagamento.data_pagamento) {
            pagamento.valor = novoValorParcela;
          }
        });
  
      };
  
  
      const isCheque = (formaPagamentoId) => {
          const forma = formasPagamento.value.find(fp => fp.id === formaPagamentoId);
          const isChequePagamento = forma ? forma.nome.toLowerCase() === "cheque" : false;
          return isChequePagamento;
      };
  
      const existeCheque = () => {
        return matricula.pagamentos.some(pagamento => pagamento.forma_pagamento_id === "Cheque");
      };
  
      const removerPagamento = (index) => {
  
        const pagamento = matricula.pagamentos[index];
  
        // 🚫 Se o pagamento já foi quitado, não pode ser removido
        if (pagamento.data_pagamento) {
          alert("❌ Não é possível excluir um pagamento que já foi quitado.");
          return;
        }
  
        // Obtém apenas os pagamentos não quitados
        const naoPagos = matricula.pagamentos.filter(p => !p.data_pagamento);
  
        if (naoPagos.length === 1) {
          alert("⚠️ Não é possível excluir a última parcela não paga.");
          return;
        }
  
        // ✂️ Remove o pagamento da lista
        matricula.pagamentos.splice(index, 1);
  
        // 🔄 Recalcula os valores das parcelas restantes
        recalcularParcelas();
      };
  
      const descontoDesabilitado = computed(() => {
        return matricula.pagamentos.some(p => p.data_pagamento); // Retorna `true` se existir algum pagamento efetuado
      });
  
      const carregarMatricula = async () => {
        if (!route.params.id) return;

        try {
          // 1) Busca dados da matrícula
          const { data: mat } = await axios.get(`/operacional/matriculas/${route.params.id}`);
          Object.assign(matricula, mat);

          // 2) Normaliza datas para inputs
          ['inicio_vigencia','termino_vigencia','data_matricula','primeiro_vencimento','data_vencimento']
            .forEach(key => {
              if (matricula[key]) {
                matricula[key] = globalUtils.formatarDataParaInput(matricula[key]);
              }
            });

          // 3) Carrega formas de pagamento (só nomes)
          await carregarFormasPagamento();

          // 4) Carrega dados do Aluno
          if (matricula.aluno_id) {
            try {
              const { data: alu } = await axios.get(`/cadastros/pessoas-fisicas/${matricula.aluno_id}`);
              Object.assign(aluno, alu);
              if (aluno.data_nascimento) {
                const [y,m,d] = aluno.data_nascimento.split('T')[0].split('-');
                aluno.data_nascimento = `${d}/${m}/${y}`;
              }
              // Carrega estado e município do aluno…
              if (aluno.estado_cod) {
                const { data: estados } = await axios.get('/localidades/estados');
                const est = estados.find(e => e.id == aluno.estado_cod);
                aluno.estado_nome = est ? est.nome : aluno.estado_cod;
              }
              if (aluno.estado_cod && aluno.municipio_cod) {
                const { data: munis } = await axios.get(`/localidades/municipios/${aluno.estado_cod}`);
                const m = munis.find(x => x.id == aluno.municipio_cod);
                aluno.municipio_nome = m ? m.nome : aluno.municipio_cod;
              }
            } catch {
              aluno.nome = "Aluno não encontrado";
            }
          }

          // 5) Carrega dados do Responsável
          if (matricula.responsavel_id) {
            try {
              const { data: resp } = await axios.get(`/cadastros/pessoas-fisicas/${matricula.responsavel_id}`);
              Object.assign(responsavel, resp);
              if (responsavel.data_nascimento) {
                const [y,m,d] = responsavel.data_nascimento.split('T')[0].split('-');
                responsavel.data_nascimento = `${d}/${m}/${y}`;
              }
              // Carrega estado e município do responsável…
              if (responsavel.estado_cod) {
                const { data: estados } = await axios.get('/localidades/estados');
                const est = estados.find(e => e.id == responsavel.estado_cod);
                responsavel.estado_nome = est ? est.nome : responsavel.estado_cod;
              }
              if (responsavel.estado_cod && responsavel.municipio_cod) {
                const { data: munis } = await axios.get(`/localidades/municipios/${responsavel.estado_cod}`);
                const m = munis.find(x => x.id == responsavel.municipio_cod);
                responsavel.municipio_nome = m ? m.nome : responsavel.municipio_cod;
              }
            } catch {
              responsavel.nome = "Responsável não encontrado";
            }
          }

          // 6) Carrega registro de pós-venda e histórico
          const { data: pv } = await axios.get('/operacional/posvendas', { params: { matricula_id: matricula.id } });
          matricula.posvendas = Array.isArray(pv.data || pv) ? (pv.data || pv) : [];
          await carregarHistoricoPosvenda();

          // 7) Históricos de trocas e renovações, turmas etc.
          carregarTiposPlano();
          carregarHistoricoTrocasPlanos();

          const { data: turmas } = await axios.get(`/operacional/matriculas-turmas?matricula_id=${matricula.id}`);
          matricula.turmas = Array.isArray(turmas) ? turmas.map(t => ({
            turma_id:       `${t.turma_id}-${t.dia_semana}-${t.horario}`,
            dia:            t.dia_semana,
            horario:        t.horario,
            instrutor:      t.instrutor_nome || t.instrutor || "Não informado",
            turma_completa: t.turma_completa
          })) : [];


          // 6) pagamento de MATRÍCULA (único registro)
          const { data: pagoMat } = await axios.get(
            `/operacional/matriculas-pagamentos/${matricula.id}`
          );
          // vamos criar um objeto separado só para o pagamento da matrícula
          matricula.pagamento_matricula = pagoMat
            ? {
                id:                 pagoMat.id,
                forma_pagamento_id: pagoMat.forma_pagamento_id,
                data_vencimento:    globalUtils.formatarDataParaInput(pagoMat.data_vencimento),
                data_pagamento:     pagoMat.data_pagamento
                                      ? globalUtils.formatarDataParaInput(pagoMat.data_pagamento)
                                      : '',
                valor:              parseFloat(pagoMat.valor).toFixed(2),
                conta_financeira_id: pagoMat.conta_financeira_id != null
                                        ? String(pagoMat.conta_financeira_id)
                                        : '',
                banco_numero:       pagoMat.banco_numero || '',
                agencia:            pagoMat.agencia || '',
                cheque_numero:      pagoMat.cheque_numero || '',
                observacoes:        pagoMat.observacoes || ''
              }
            : {
                // valores padrão, caso não exista
                forma_pagamento_id: null,
                data_vencimento:    '',
                data_pagamento:     '',
                valor:              '0.00',
                conta_financeira_id:'',
                banco_numero:       '',
                agencia:            '',
                cheque_numero:      '',
                observacoes:        ''
              };

          // 5) -- MENSALIDADES --
          const { data: parcelasRaw } = await axios.get(
            `/operacional/pagamentos-matriculas/${matricula.id}`
          );
          const parcelas = Array.isArray(parcelasRaw) ? parcelasRaw : [];
          matricula.pagamentos = parcelas
            .filter(p => !p.pgt_matricula)    // só mensalidades
            .map(p => ({
              ...p,
              forma_pagamento_id:  p.forma_pagamento_id,
              data_vencimento:     globalUtils.formatarDataParaInput(p.data_vencimento),
              data_pagamento:      p.data_pagamento
                                      ? globalUtils.formatarDataParaInput(p.data_pagamento)
                                      : '',
              valor:               parseFloat(p.valor).toFixed(2),
              conta_financeira_id: p.conta_financeira_id != null
                                      ? String(p.conta_financeira_id)
                                      : '',
              banco_numero:        p.banco_numero || '',
              agencia:             p.agencia || '',
              cheque_numero:       p.cheque_numero || '',
              adicional:           p.adicional
            }));

        } catch (err) {
          console.error("❌ Erro ao carregar matrícula:", err);
        } finally {
          isLoading.value = false;
        }
      };
  
      const responsavelSexoFormatado = computed(() => {
        if (responsavel.sexo === 'F') return 'Feminino';
        if (responsavel.sexo === 'M') return 'Masculino';
        return responsavel.sexo || '';
      });
  
      const alunoSexoFormatado = computed(() => {
        if (aluno.sexo === 'F') return 'Feminino';
        if (aluno.sexo === 'M') return 'Masculino';
        return aluno.sexo || '';
      });
  
      // Função auxiliar para formatar o CPF
      const formatarCPF = (cpf) => {
        if (!cpf) return '';
        // Remove todos os caracteres que não são dígitos
        cpf = cpf.replace(/\D/g, '');
        // Se o CPF não tiver 11 dígitos, retorna sem formatação
        if (cpf.length !== 11) return cpf;
        // Aplica a máscara: 000.000.000-00
        return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      };
  
      // Propriedades computadas para os CPFs formatados do aluno e do responsável
      const alunoCPFFormatado = computed(() => formatarCPF(aluno.cpf));
      const responsavelCPFFormatado = computed(() => formatarCPF(responsavel.cpf));
  
      const editarPessoa = (tipo) => {
        let pessoaId = null;
        if (tipo === 'responsavel') {
          pessoaId = matricula.responsavel_id;
        } else if (tipo === 'aluno') {
          pessoaId = matricula.aluno_id;
        }
        const returnUrl = `/operacional/matriculas/${matricula.id}/detalhar`;
        router.push({ 
          name: 'EditarPessoaFisica', 
          params: { id: pessoaId }, 
          query: { returnUrl } 
        });
      };
  
      const getCategoriaNome = (id) => {
        const cat = categorias.value.find(c => c.id === id);
        return cat ? cat.nome : 'Não informado';
      };
  
      const getTipoPlanoDescricao = (id) => {
        const tipo = tiposPlano.value.find(t => t.id === id);
        return tipo ? `${tipo.nome} - ${tipo.duracao} dias` : 'Não informado';
      };
  
      const getNivelDescricao = (id) => {
        const nivel = niveis.value.find(n => n.id === id);
        return nivel ? nivel.nivel : 'Não informado';
      };
  
      const statusExibido = computed(() => {
        if (!matricula.termino_vigencia) {
          return '';
        }
        const hoje = new Date();
        if (matricula.status) {
          if (matricula.status.toLowerCase() === 'arquivado') {
            return matricula.status;
          }
        }
        const termino = new Date(matricula.termino_vigencia);
        if (hoje > termino) {
          return 'Encerrado';
        }
        if (matricula.inicio_trancamento && matricula.termino_trancamento) {
          const inicio = new Date(matricula.inicio_trancamento);
          const fim = new Date(matricula.termino_trancamento);
          if (hoje >= inicio && hoje <= fim) {
            return 'Trancado';
          }
        }
        return 'Ativo';
      });
  
      const statusColor = computed(() => {
        switch (statusExibido.value) {
          case 'Ativo':
            return 'green';
          case 'Trancado':
            return 'red';
          case 'Arquivado':
            return 'blue';
          case 'Encerrado':
            return 'brown';
          default:
            return 'inherit';
        }
      });
  
      function getTurmaInfo(turmasDisponiveis) {
        // Verifica se o parâmetro é um array
        if (Array.isArray(turmasDisponiveis)) {
          const turmasString = turmasDisponiveis.map(turma => {
            // Se o objeto já possuir a string completa, retorna-a
            if (turma.turma_completa && turma.turma_completa.trim() !== "") {
              return turma.turma_completa;
            }
            const id = turma.id || 'Não informado';
            const dia = turma.dia || 'Não informado';
            const horario = turma.horario || 'Não informado';
            const instrutor = turma.instrutor || 'Não informado';
            return `Turma: ${id} | Dia: ${dia} | Hora: ${horario} | Instrutor: ${instrutor}`;
          });
  
          // Junta as strings individuais com <br/> para quebra de linha
          return turmasString.join('<br/>');
        }
  
        // Se for um objeto único
        if (turmasDisponiveis.turma_completa && turmasDisponiveis.turma_completa.trim() !== "") {
          return turmasDisponiveis.turma_completa;
        }
        const id = turmasDisponiveis.id || 'Não informado';
        const dia = turmasDisponiveis.dia || 'Não informado';
        const horario = turmasDisponiveis.horario || 'Não informado';
        const instrutor = turmasDisponiveis.instrutor || 'Não informado';
        return `Turma: ${id} | Dia: ${dia} | Hora: ${horario} | Instrutor: ${instrutor}`;
      }
  
   
  
      const carregarPosVendas = async () => {
        try {
          // Supondo que o endpoint espera o id da matrícula como query string,
          // por exemplo: /operacional/posvendas?matricula_id=ID
          const response = await axios.get(`/operacional/posvendas`, {
            params: { matricula_id: matricula.id }
          });
          // Atribua os dados retornados à propriedade posvendas da matrícula
          matricula.posvendas = response.data.data; // Certifique-se que o endpoint retorne um array
        } catch (error) {
          console.error("Erro ao carregar dados de pós-venda:", error);
        }
      };
  
  
      const cancelar = () => {
        router.push('/operacional/posvendas');
      };
      
      watch(
        () => [matricula.categoria_id, matricula.tipo_plano_id],
        async ([novaCategoria, novoTipo]) => {
          if (novaCategoria && novoTipo) {
            try {
              const response = await axios.get(`/cadastros/planos-matriculas`, {
                params: {
                  categoria_id: novaCategoria,
                  tipo_id: novoTipo,
                },
              });
  
              if (response.data.data && response.data.data.length > 0) {
                const planoEncontrado = response.data.data[0];
                plano.aulas_semana = planoEncontrado.aulas_semana ?? 0;
                plano.quantidade_parcelas = planoEncontrado.quantidade_parcelas ?? 0;
              } else {
                // Garantindo que os valores sejam resetados corretamente
                plano.aulas_semana = 0;
                plano.quantidade_parcelas = 0;
              }
  
              // 🔄 Atualiza o Término da Vigência se já houver uma Data de Início definida
              if (matricula.inicio_vigencia) {
  
                // Encontrar o plano selecionado para obter a duração correta
                const planoSelecionado = tiposPlano.value.find(p => p.id === novoTipo);
  
                if (planoSelecionado) {
                  const inicio = new Date(matricula.inicio_vigencia);
                  inicio.setDate(inicio.getDate() + planoSelecionado.duracao); // Adiciona os dias da duração
  
                  matricula.termino_vigencia = inicio.toISOString().split('T')[0]; // Formata no padrão YYYY-MM-DD
                }
              }
  
            } catch (error) {
              console.error("Erro ao buscar plano de matrícula:", error);
            }
          }
        }
      );
  
      watch(() => matricula.nivel_id, (novoNivel) => {
        if (novoNivel) {
          carregarTurmas();
          matricula.turmas = []; // Reseta turmas selecionadas ao trocar o nível
        }
      });
  
      watch(
        () => plano.aulas_semana,
        (novoValor) => {
          matricula.aulas_semana = novoValor;
        }
      );
  
      watch(() => matricula.tipo_plano_id, (novoValor) => {
  
        // Encontrar o tipo de plano correspondente
        const planoSelecionado = tiposPlano.value.find(p => p.id === novoValor);
  
        if (planoSelecionado) {
          matricula.valor_mensalidade = planoSelecionado.valor_mensalidade;
          matricula.quantidade_parcelas = planoSelecionado.quantidade_parcelas;
        }
  
        carregarDadosPlanoMatricula();
      });
  
      watch([() => matricula.valor, () => matricula.desconto], ([novoValor, novoDesconto]) => {
        if (novoValor !== null && novoValor !== undefined) {
          matricula.valor_com_desconto = novoValor - (novoValor * (novoDesconto || 0) / 100);
        }
      });
  
      watch(() => matricula.convenio_id, (novoConvenioId) => {
        if (!novoConvenioId) {
          matricula.desconto = 0;
        }
      });
  
      watch(() => plano.valor_mensalidade, calcularValorComDescontoMensalidade);
  
      watch(() => matricula.inicio_vigencia, (novaData) => {
        if (novaData) {
         
          // Verifica se já temos um número de aulas por semana definido
          if (plano.aulas_semana > 0) {
            matricula.turmas = Array.from({ length: plano.aulas_semana }, () => ({
              turma_id: "",
              id: null,
              dia: "",
              horario: "",
              instrutor: "",
            }));
          }
        }
      });
  
      watch([() => matricula.valor_mensalidade, () => matricula.desconto_mensalidade], ([novoValor, novoDesconto]) => {
        if (novoValor !== null && novoValor !== undefined) {
          matricula.valor_com_desconto_mensalidade = novoValor - (novoValor * (novoDesconto || 0) / 100);
        }
      });
  
      watch(() => matricula.primeiro_vencimento, (novaData) => {
          if (!matricula.id && novaData) { // 🔹 Apenas se for nova matrícula
              distribuirParcelasMensalidade();
          }
      });
  
      watch(() => plano.quantidade_parcelas, (novoValor) => {
        matricula.quantidade_pagamentos = novoValor; // Certificar que o campo seja atualizado corretamente
        calcularValorTotalMensalidade(); // Atualiza o valor total
      });
  
      onMounted(async () => {
        await carregarCategorias();
        await carregarNiveis();
        await carregarValorMatricula();
        await carregarFormasPagamento();
        await carregarConvenios();
        await carregarContasFinanceiras();
        if (route.params.id) {
          await carregarMatricula(route.params.id);
          await carregarPosVendas();
          await carregarHistoricoTrocasPlanos();
        }
        isLoading.value = false;
      });
  
      return {
  
        isLoading,
        statusExibido,
        statusColor,
  
        historicoTrocas,
        historicoTrocasPlanos,
  
        getTurmaInfo,
        getCategoriaNome,
        getTipoPlanoDescricao,
        getNivelDescricao,
        editarPessoa,
        alunoCPFFormatado,
        responsavelCPFFormatado,
        alunoSexoFormatado,
        responsavelSexoFormatado,
        responsavel,
        aluno,
        route,
        router,
        globalUtils,
        matricula,
        novaTurma,
        categorias,
        tiposPlano,
        plano,
        niveis,
        aulasSemanaOptions,
        parcelasOptions,
        turmas,
        carregarCategorias,
        carregarTiposPlano,
        carregarNiveis,
        carregarTurmas,
        carregarDadosPlanoMatricula,
        calcularTerminoVigencia,
        removerTurma,
        turmasDisponiveis,
        cancelar,
  
        carregarValorMatricula,
        formasPagamento,
        convenios,
        calcularValorComDesconto,
        atualizarDescontoConvenio,
  
        calcularValorComDescontoMensalidade,
        calcularValorTotalMensalidade,
        distribuirParcelasMensalidade,
        podeAdicionarPagamento,
        adicionarPagamento,
        isCheque,
        existeCheque,
        removerPagamento,
        descontoDesabilitado,
        utils: globalUtils,
        pagamentoMatricula,
        contaFinanceiraFormatada,
        contaFormatada,
        historicoPosvenda,
        registroPosvenda,

      };
    }
  };
  </script>
  
  <style scoped>
  .section-title {
    font-size: 0.8rem;
    font-weight: bold;
    margin-bottom: 10px;
  }
  
  /* Estilização para campos desativados */
  .disabled-input {
    background: #e9ecef;
    border: 1px solid #ced4da;
  }
  
  /* Ajuste dos botões */
  .btn-sm {
    height: 38px;
  }
  
  /* Estilização para a seção das turmas */
  .turma-item {
    background: white;
    padding: 8px;
    border-radius: 5px;
    border: 1px solid #ccc;
  }
  
  /* Ícones de ação */
  .required {
    color: red;
  }
  
  .add-turma-container {
    display: flex;
    justify-content: flex-end; /* Alinha o botão à direita */
    margin-top: 5px; /* Pequeno espaçamento acima */
  }
  
  
  /* Estilização da chave de alternância */
  .toggle-switch {
    position: relative;
    display: inline-flex;
    align-items: center;
    width: 50px;
    height: 25px;
  }
  
  /* Esconde o input nativo */
  .toggle-input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  /* Estilização do botão */
  /* Container do switch alinhado com os demais campos */
  .toggle-switch {
    display: flex;
    align-items: center;
  }
  
  .toggle-switch label {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 6px 15px;  /* Ajuste do padding interno */
    min-width: 60px;  /* Largura mínima para acomodar o texto */
    border-radius: 20px; 
    font-weight: bold;
  }
  
  /* Esconde o input nativo */
  .toggle-input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  /* Estilização do botão */
  .toggle-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 100%;
    background-color: #949596; 
    border-radius: 50px;
    cursor: pointer;
    transition: background-color 0.3s ease;
    padding: 4px 16px;
    font-weight: bold;
    color: white;
    font-size: 14px;
    text-align: center;
    position: relative;
  }
  
  /* Mudança de cor para "Sim" (Pagamento Pendente) */
  .toggle-input:checked + .toggle-label {
    background-color: #949596; 
  }
  
  /* Movimentação do botão ao alternar */
  .toggle-label:before {
    content: "";
    position: absolute;
    left: 5px;
    width: 18px;
    height: 18px;
    background: white;
    border-radius: 50%;
    transition: transform 0.3s ease;
  }
  
  /* Ajusta a posição do botão quando ativado */
  .toggle-input:checked + .toggle-label:before {
    transform: translateX(35px);
  }
  
  /* Ajuste do texto dentro do botão */
  .toggle-label span {
    flex: 1;
    text-align: center;
    font-size: 12px;
    white-space: nowrap;
  }
  
  .icons-container {
    display: flex;
    align-items: center;
    gap: 5px; /* Espaçamento entre os ícones */
    margin-left: auto; /* Alinha os ícones à direita */
  }
  
  .icons-container i {
    cursor: pointer;
    font-size: 1.2rem; /* Ajuste do tamanho do ícone, se necessário */
  }

  .historico-container {
  padding: 0.75rem;
  background-color: #f9f9f9;
  border-radius: 4px;
  border: 1px solid #ccc;
  margin-bottom: 0.5rem;
}

.historico-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.historico-title {
  font-size: 0.8rem;
  font-weight: 600;
  margin: 0;
  color: #333;
}

.historico-content {
  width: 100%;
  display: block;
  line-height: 1.2;
}

  
  </style>
  